import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import { Message, Profile } from '../types';
import Layout from '../components/Layout';
import { ChevronLeftIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { BellIcon, BellSlashIcon } from '@heroicons/react/24/outline';

const Chat = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [recipients, setRecipients] = useState<Profile[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const { unreadCounts, markMessagesAsRead, refreshUnreadStatus } = useUnreadMessages();
  const { playNotification, soundEnabled, toggleSound } = useNotificationSound();
  const [showUserList, setShowUserList] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) {
      fetchRecipients();
      refreshUnreadStatus();
    }
  }, [user]);

  useEffect(() => {
    if (user && selectedRecipient) {
      console.log('Setting up chat for:', { user: user.id, recipient: selectedRecipient.id });
      fetchMessages();
      
      const channelId = `chat:${user.id}:${selectedRecipient.id}`;
      const channel = supabase.channel(channelId);

      channel
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages'
          },
          async (payload) => {
            console.log('New message received:', payload);
            const newMessage = payload.new as Message;
            
            // Verificar si el mensaje pertenece a la conversación actual
            if (
              (newMessage.sender_id === user.id && newMessage.receiver_id === selectedRecipient.id) ||
              (newMessage.sender_id === selectedRecipient.id && newMessage.receiver_id === user.id)
            ) {
              console.log('Message belongs to current conversation, updating state');
              setMessages(prevMessages => {
                // Evitar duplicados
                const messageExists = prevMessages.some(msg => msg.id === newMessage.id);
                if (messageExists) {
                  return prevMessages;
                }
                return [...prevMessages, newMessage];
              });

              // Si el mensaje es para el usuario actual, marcarlo como leído
              if (newMessage.receiver_id === user.id) {
                await markMessagesAsRead(newMessage.sender_id);
              }
            } else if (newMessage.receiver_id === user.id) {
              // Si el mensaje es para el usuario actual pero no está en esa conversación,
              // actualizar el contador de no leídos
              await refreshUnreadStatus();
            }
          }
        )
        .subscribe((status) => {
          console.log(`Subscription status for ${channelId}:`, status);
        });

      return () => {
        console.log(`Unsubscribing from ${channelId}`);
        supabase.removeChannel(channel);
      };
    }
  }, [user, selectedRecipient]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${user?.id}`
        },
        (payload: any) => {
          // Solo reproducir sonido si el mensaje es nuevo y no es del usuario actual
          if (payload.new && payload.new.sender_id !== user?.id) {
            playNotification();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, playNotification]);

  useEffect(() => {
    if (selectedRecipient && user) {
      markMessagesAsRead(selectedRecipient.id);
    }
  }, [selectedRecipient, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchRecipients = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .neq('id', user?.id)
        .order('name');

      if (error) throw error;
      setRecipients(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!selectedRecipient || !user) return;

    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(
          `and(sender_id.eq.${user.id},receiver_id.eq.${selectedRecipient.id}),` +
          `and(sender_id.eq.${selectedRecipient.id},receiver_id.eq.${user.id})`
        )
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchUnreadCounts = async () => {
    if (!user) return;

    try {
      // Primero obtenemos todos los mensajes no leídos
      const { data, error } = await supabase
        .from('messages')
        .select('sender_id')
        .eq('receiver_id', user.id)
        .eq('read', false);

      if (error) throw error;

      // Luego contamos manualmente por sender_id
      const counts: Record<string, number> = {};
      data.forEach(message => {
        counts[message.sender_id] = (counts[message.sender_id] || 0) + 1;
      });
      
      // setUnreadCounts(counts);
    } catch (error) {
      console.error('Error fetching unread counts:', error);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedRecipient || !newMessage.trim()) return;

    try {
      const now = new Date().toISOString();
      const { data: message, error } = await supabase
        .from('messages')
        .insert([
          {
            sender_id: user.id,
            receiver_id: selectedRecipient.id,
            message: newMessage.trim(),
            read: false,
            created_by: user.id,
            updated_by: user.id,
            created_at: now,
            updated_at: now
          }
        ])
        .select()
        .single();

      if (error) throw error;

      console.log('Message sent successfully:', message);
      setNewMessage('');
      
      // Actualizar el estado local de mensajes
      setMessages(prevMessages => [...prevMessages, message]);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleRecipientSelect = async (recipient: Profile) => {
    setSelectedRecipient(recipient);
    setShowUserList(false);
    // Marcar mensajes como leídos cuando se selecciona un chat
    if (unreadCounts[recipient.id]) {
      await markMessagesAsRead(recipient.id);
    }
  };

  const handleBackToList = () => {
    setShowUserList(true);
  };

  const formatMessageTime = (message: Message): string => {
    const timestamp = message.created_at || message.timestamp || new Date().toISOString();
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] flex flex-col bg-gray-50">
        <div className="flex-1 flex overflow-hidden">
          {/* Lista de usuarios (visible en desktop y en móvil cuando showUserList es true) */}
          <div className={`
            ${showUserList ? 'flex w-full' : 'hidden'} 
            sm:flex sm:w-80 lg:w-96 
            bg-white border-r border-gray-200 
            flex-col absolute sm:relative
            h-full z-10
          `}>
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Conversaciones</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {recipients.map((recipient) => (
                <div
                  key={recipient.id}
                  className={`
                    p-4 cursor-pointer flex items-center space-x-3
                    hover:bg-gray-50 active:bg-gray-100 transition-colors duration-150
                    ${selectedRecipient?.id === recipient.id ? 'bg-indigo-50' : ''}
                  `}
                  onClick={() => handleRecipientSelect(recipient)}
                >
                  <div className="relative">
                    <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg font-medium">
                        {recipient.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    {unreadCounts[recipient.id] > 0 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">{unreadCounts[recipient.id]}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{recipient.name}</p>
                    <p className="text-sm text-gray-500 truncate">{recipient.email}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Área de chat */}
          <div className={`
            ${!showUserList ? 'flex w-full' : 'hidden'}
            sm:flex 
            flex-1 
            flex-col 
            bg-gray-50
            h-full
          `}>
            {selectedRecipient ? (
              <>
                {/* Header del chat */}
                <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
                  <div className="px-4 py-3 flex items-center space-x-3">
                    <button
                      onClick={handleBackToList}
                      className="sm:hidden -ml-1 p-2 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors duration-150"
                    >
                      <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {selectedRecipient.name}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">
                        {selectedRecipient.email}
                      </p>
                    </div>
                    <button
                      onClick={toggleSound}
                      className="p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      title={soundEnabled ? 'Deshabilitar sonido' : 'Habilitar sonido'}
                    >
                      {soundEnabled ? (
                        <BellIcon className="h-5 w-5 text-gray-600" />
                      ) : (
                        <BellSlashIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Área de mensajes */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`
                          max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2.5
                          ${message.sender_id === user?.id
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-gray-900 shadow-sm'
                          }
                        `}
                      >
                        <p className="text-[15px] leading-tight break-words">{message.message}</p>
                        <p className={`text-xs mt-1.5 ${
                          message.sender_id === user?.id
                            ? 'text-indigo-200'
                            : 'text-gray-500'
                        }`}>
                          {formatMessageTime(message)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input de mensaje */}
                <div className="bg-white border-t border-gray-200 p-3 sm:p-4 sticky bottom-0">
                  <form onSubmit={handleSendMessage} className="flex space-x-2">
                    <input
                      ref={messageInputRef}
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={`Mensaje para ${selectedRecipient.name}...`}
                      className="flex-1 rounded-full border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-[15px]"
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="w-11 h-11 flex items-center justify-center bg-indigo-600 text-white rounded-full hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors duration-150"
                    >
                      <PaperAirplaneIcon className="h-5 w-5 transform rotate-90" />
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500 p-4 text-center">
                <p>Selecciona un usuario para comenzar a chatear</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Chat;
